import { useState } from 'react';
import { Card, CardContent, Typography, Grid, Box, Alert, IconButton, Chip } from '@mui/material';
import { keyframes } from '@mui/system';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MetricExplanationModal from './MetricExplanationModal';

// Equipment type icons
const equipmentIcons = {
  Chiller: '❄️',
  RTU: '🏢',
  CRAC: '🖥️',
  Split: '💨',
  Unknown: '❓',
};

// Smooth entrance animation
const smoothSlideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(24px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Subtle background flow
const subtleFlow = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

// Staggered card reveal
const fadeSlideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

/**
 * PortfolioSummary - Unified Equipment ROI Summary
 *
 * Displays integrated equipment + value metrics: equipment count, tonnage,
 * treatment cost, payback, savings, value breakdown, and equipment type cards.
 *
 * @param {Object} data - Portfolio data from ROI calculations
 * @param {Object} byTypeData - Equipment breakdown by type (optional)
 * @param {string} paymentMode - 'upfront' or 'threeYear'
 * @param {Object} paymentMetrics - Payment metrics for display adjustments
 */
export default function PortfolioSummary({ data, byTypeData = null, paymentMode = 'upfront', paymentMetrics = null }) {
  const [openMetric, setOpenMetric] = useState(null);

  if (!data) {
    console.error('PortfolioSummary: Missing required data prop');
    return null;
  }

  if (!data.summary) {
    console.error('PortfolioSummary: Missing data.summary', data);
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        Unable to display portfolio summary: missing summary data
      </Alert>
    );
  }

  const {
    total_equipment,
    total_tonnage,
    total_treatment_cost,
    total_annual_savings,
    total_annual_value, // Comprehensive value (5 corpus-validated components)
    total_avoided_costs, // Sum of avoided cost components
    weighted_average_payback_months,
    energy_only_payback_months, // NEW: Legacy payback for comparison
    five_year_roi,
    total_kwh_saved,
  } = data.summary || {};

  const { co2_avoided_tonnes, vehicle_equivalent } = data.environmental || {};
  const { year_5_cumulative, year_5_net, year_5_cumulative_value } = data.projections || {};
  const valueBreakdown = data.value_breakdown || {};
  const valueComposition = data.value_composition || {};

  // Use comprehensive value if available, otherwise fall back to energy savings
  const displayValue = total_annual_value || total_annual_savings;
  const displayPayback = weighted_average_payback_months;
  const hasComprehensiveValue = total_annual_value && total_annual_value !== total_annual_savings;

  // Format helpers
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatNumber = (value, decimals = 0) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value);
  };

  // Filter active equipment types
  const activeTypes = byTypeData ? Object.entries(byTypeData).filter(([_, metrics]) => metrics.count > 0) : [];

  return (
    <Box sx={{ mb: 6 }}>
      {/* Section Header */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            letterSpacing: '-0.01em',
            mb: 0.5,
          }}
        >
          Equipment ROI Summary
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Complete value analysis with corpus-validated metrics
        </Typography>
      </Box>

      {/* Hero Stat - Total Annual Value (Refined Data Elegance) */}
      <Box
        sx={{
          textAlign: 'center',
          mb: 2,
          py: 2.5,
          px: 2.5,
          borderRadius: 2,
          background: (theme) => theme.palette.mode === 'dark'
            ? 'linear-gradient(180deg, rgba(160, 206, 78, 0.04) 0%, rgba(10, 12, 14, 0.6) 100%)'
            : 'linear-gradient(180deg, rgba(91, 127, 36, 0.06) 0%, #FAFAFA 100%)',
          border: (theme) => `1px solid ${theme.palette.mode === 'dark'
            ? 'rgba(160, 206, 78, 0.12)'
            : 'rgba(91, 127, 36, 0.2)'}`,
          position: 'relative',
          overflow: 'hidden',
          // Subtle inner glow
          boxShadow: (theme) => theme.palette.mode === 'dark'
            ? 'inset 0 1px 0 rgba(160, 206, 78, 0.1), 0 4px 24px rgba(0, 0, 0, 0.2)'
            : 'inset 0 1px 0 rgba(91, 127, 36, 0.08), 0 4px 24px rgba(0, 0, 0, 0.06)',
          // Top accent line
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '120px',
            height: '2px',
            background: (theme) => theme.palette.mode === 'dark'
              ? 'linear-gradient(90deg, transparent, #A0CE4E, transparent)'
              : 'linear-gradient(90deg, transparent, #5B7F24, transparent)',
            opacity: 0.8,
          },
        }}
      >
        {/* Label Row */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, mb: 0.75 }}>
          <Typography
            sx={{
              fontSize: '0.65rem',
              fontWeight: 500,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: (theme) => theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.45)'
                : 'rgba(0, 0, 0, 0.55)',
            }}
          >
            {hasComprehensiveValue ? 'Total Annual Value' : 'Annual Energy Savings'}
          </Typography>
          <IconButton
            size="small"
            onClick={() => setOpenMetric(hasComprehensiveValue ? 'total_annual_value' : 'energy_savings')}
            sx={{
              p: 0.25,
              color: (theme) => theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.35)'
                : 'rgba(0, 0, 0, 0.4)',
              '&:hover': {
                color: (theme) => theme.palette.mode === 'dark' ? '#A0CE4E' : '#5B7F24',
                background: (theme) => theme.palette.mode === 'dark'
                  ? 'rgba(160, 206, 78, 0.1)'
                  : 'rgba(91, 127, 36, 0.1)'
              }
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 12 }} />
          </IconButton>
        </Box>

        {/* Main Value - Refined size with subtle glow */}
        <Typography
          sx={{
            fontSize: { xs: '1.75rem', sm: '2rem' },
            fontWeight: 700,
            lineHeight: 1,
            letterSpacing: '-0.02em',
            color: (theme) => theme.palette.mode === 'dark' ? '#A0CE4E' : 'text.primary',
            fontFamily: '"JetBrains Mono", monospace',
            fontVariantNumeric: 'tabular-nums',
            textShadow: (theme) => theme.palette.mode === 'dark'
              ? '0 0 40px rgba(160, 206, 78, 0.3)'
              : 'none',
            mb: 0.25,
          }}
        >
          {formatCurrency(displayValue)}
        </Typography>

        {/* Subtitle */}
        <Typography
          sx={{
            fontSize: '0.75rem',
            color: (theme) => theme.palette.mode === 'dark'
              ? 'rgba(255, 255, 255, 0.5)'
              : 'rgba(0, 0, 0, 0.6)',
            fontWeight: 400,
            letterSpacing: '0.01em',
          }}
        >
          {hasComprehensiveValue
            ? 'per year (energy savings + avoided costs)'
            : 'per year in reduced electricity costs'}
        </Typography>

        {/* Value Breakdown Pills - Refined inline style */}
        {hasComprehensiveValue && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 1.5,
              mt: 1.5,
              flexWrap: 'wrap',
            }}
          >
            {/* Energy Savings Pill */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                px: 1.5,
                py: 0.75,
                borderRadius: 1.5,
                background: (theme) => theme.palette.mode === 'dark'
                  ? 'rgba(76, 175, 80, 0.08)'
                  : 'rgba(91, 127, 36, 0.1)',
                border: (theme) => `1px solid ${theme.palette.mode === 'dark'
                  ? 'rgba(76, 175, 80, 0.2)'
                  : 'rgba(91, 127, 36, 0.25)'}`,
              }}
            >
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: (theme) => theme.palette.mode === 'dark' ? '#4CAF50' : '#5B7F24' }} />
              <Typography sx={{ fontSize: '0.7rem', color: (theme) => theme.palette.mode === 'dark' ? '#6FCF7C' : 'text.primary', fontWeight: 600, fontFamily: '"JetBrains Mono", monospace' }}>
                {formatCurrency(total_annual_savings)}
              </Typography>
              <Typography sx={{ fontSize: '0.65rem', color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.5)' }}>
                Energy ({valueComposition?.energy_savings_pct?.toFixed(0) || 0}%)
              </Typography>
              <IconButton
                size="small"
                onClick={() => setOpenMetric('energy_savings')}
                sx={{
                  p: 0.25,
                  ml: 0.25,
                  color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.4)',
                  '&:hover': {
                    color: (theme) => theme.palette.mode === 'dark' ? '#6FCF7C' : '#5B7F24',
                    background: (theme) => theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.15)' : 'rgba(91, 127, 36, 0.15)'
                  }
                }}
              >
                <InfoOutlinedIcon sx={{ fontSize: 11 }} />
              </IconButton>
            </Box>

            {/* Avoided Costs Pill */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                px: 1.5,
                py: 0.75,
                borderRadius: 1.5,
                background: (theme) => theme.palette.mode === 'dark'
                  ? 'rgba(78, 205, 196, 0.08)'
                  : 'rgba(13, 115, 119, 0.1)',
                border: (theme) => `1px solid ${theme.palette.mode === 'dark'
                  ? 'rgba(78, 205, 196, 0.2)'
                  : 'rgba(13, 115, 119, 0.25)'}`,
              }}
            >
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: (theme) => theme.palette.mode === 'dark' ? '#4ECDC4' : '#0D7377' }} />
              <Typography sx={{ fontSize: '0.7rem', color: (theme) => theme.palette.mode === 'dark' ? '#6EE7E0' : 'text.primary', fontWeight: 600, fontFamily: '"JetBrains Mono", monospace' }}>
                {formatCurrency(total_avoided_costs)}
              </Typography>
              <Typography sx={{ fontSize: '0.65rem', color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.5)' }}>
                Avoided ({valueComposition?.avoided_costs_pct?.toFixed(0) || 0}%)
              </Typography>
              <IconButton
                size="small"
                onClick={() => setOpenMetric('total_avoided_costs')}
                sx={{
                  p: 0.25,
                  ml: 0.25,
                  color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.4)',
                  '&:hover': {
                    color: (theme) => theme.palette.mode === 'dark' ? '#6EE7E0' : '#0D7377',
                    background: (theme) => theme.palette.mode === 'dark' ? 'rgba(78, 205, 196, 0.15)' : 'rgba(13, 115, 119, 0.15)'
                  }
                }}
              >
                <InfoOutlinedIcon sx={{ fontSize: 11 }} />
              </IconButton>
            </Box>
          </Box>
        )}

        {/* Value Multiplier Insight - Shows how much more value the 5-component model captures */}
        {hasComprehensiveValue && total_annual_savings > 0 && (
          <Box
            sx={{
              mt: 1.5,
              py: 0.75,
              px: 2,
              borderRadius: 1,
              background: (theme) => theme.palette.mode === 'dark'
                ? 'rgba(255, 193, 7, 0.06)'
                : 'rgba(180, 140, 0, 0.08)',
              border: (theme) => `1px solid ${theme.palette.mode === 'dark'
                ? 'rgba(255, 193, 7, 0.15)'
                : 'rgba(180, 140, 0, 0.2)'}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: '0.7rem',
                color: (theme) => theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.7)'
                  : 'rgba(0, 0, 0, 0.7)',
                textAlign: 'center',
              }}
            >
              ⚡ This 5-component model captures{' '}
              <Typography
                component="span"
                sx={{
                  fontWeight: 700,
                  color: (theme) => theme.palette.mode === 'dark' ? '#FFC107' : 'text.primary',
                  fontFamily: '"JetBrains Mono", monospace',
                }}
              >
                {total_annual_savings > 0
                  ? (total_annual_value / total_annual_savings).toFixed(1)
                  : '1.0'}×
              </Typography>
              {' '}more value than traditional energy-only ROI calculations
            </Typography>
          </Box>
        )}
      </Box>

      {/* Equipment Stats Row */}
      <Grid container spacing={1.5} sx={{ mb: 3 }}>
        {[
          { label: 'Equipment', value: formatNumber(total_equipment), unit: 'units', colorDark: '#A0CE4E', colorLight: '#5B7F24' },
          { label: 'Tonnage', value: formatNumber(total_tonnage, 1), unit: 'tons', colorDark: '#4ECDC4', colorLight: '#0D7377' },
          { label: 'Treatment', value: formatCurrency(total_treatment_cost), unit: '', colorDark: '#FF6B35', colorLight: '#C74600' },
          { label: 'Payback', value: formatNumber(displayPayback, 1), unit: 'mo', colorDark: '#64B5F6', colorLight: '#1868DB' },
          { label: '5-Yr ROI', value: formatNumber(five_year_roi, 0), unit: '%', colorDark: '#A0CE4E', colorLight: '#5B7F24' },
        ].map((stat, idx) => (
          <Grid item xs={6} sm={4} md={2.4} key={idx}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                background: (theme) => theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.04)',
                borderTop: (theme) => `3px solid ${theme.palette.mode === 'dark' ? stat.colorDark : stat.colorLight}`,
                textAlign: 'center',
              }}
            >
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: '0.05em' }}>
                {stat.label}
              </Typography>
              <Typography
                sx={{
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  fontFamily: '"JetBrains Mono", monospace',
                  color: (theme) => theme.palette.mode === 'dark' ? stat.colorDark : 'text.primary',
                  mt: 0.5,
                }}
              >
                {stat.value}
                {stat.unit && (
                  <Typography component="span" sx={{ fontSize: '0.75rem', fontWeight: 500, color: 'text.secondary', ml: 0.25 }}>
                    {stat.unit}
                  </Typography>
                )}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* True Payback vs Energy-Only Comparison */}
      {hasComprehensiveValue && energy_only_payback_months && energy_only_payback_months > displayPayback && (
        <Box
          sx={{
            mb: 3,
            p: 2,
            borderRadius: 2,
            background: (theme) => theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, rgba(255, 193, 7, 0.04) 0%, rgba(255, 152, 0, 0.08) 100%)'
              : 'linear-gradient(135deg, rgba(180, 140, 0, 0.06) 0%, rgba(200, 150, 0, 0.1) 100%)',
            border: (theme) => `1px solid ${theme.palette.mode === 'dark'
              ? 'rgba(255, 193, 7, 0.2)'
              : 'rgba(180, 140, 0, 0.25)'}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 3,
            flexWrap: 'wrap',
          }}
        >
          {/* True Payback */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              sx={{
                fontSize: '0.6rem',
                fontWeight: 500,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: (theme) => theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.5)'
                  : 'rgba(0, 0, 0, 0.55)',
                mb: 0.5,
              }}
            >
              True Payback (5 components)
            </Typography>
            <Typography
              sx={{
                fontSize: '1.25rem',
                fontWeight: 700,
                color: (theme) => theme.palette.mode === 'dark' ? '#4CAF50' : 'text.primary',
                fontFamily: '"JetBrains Mono", monospace',
              }}
            >
              {formatNumber(displayPayback, 1)} mo
            </Typography>
          </Box>

          {/* VS Divider */}
          <Typography
            sx={{
              fontSize: '0.875rem',
              fontWeight: 600,
              color: (theme) => theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.3)'
                : 'rgba(0, 0, 0, 0.35)',
              px: 2,
            }}
          >
            vs
          </Typography>

          {/* Energy-Only Payback */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              sx={{
                fontSize: '0.6rem',
                fontWeight: 500,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: (theme) => theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.5)'
                  : 'rgba(0, 0, 0, 0.55)',
                mb: 0.5,
              }}
            >
              Energy-Only Payback
            </Typography>
            <Typography
              sx={{
                fontSize: '1.25rem',
                fontWeight: 700,
                color: (theme) => theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.4)'
                  : 'rgba(0, 0, 0, 0.4)',
                fontFamily: '"JetBrains Mono", monospace',
                textDecoration: 'line-through',
                textDecorationColor: (theme) => theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.2)'
                  : 'rgba(0, 0, 0, 0.25)',
              }}
            >
              {formatNumber(energy_only_payback_months, 1)} mo
            </Typography>
          </Box>

          {/* Insight */}
          <Box
            sx={{
              flex: '1 1 100%',
              textAlign: 'center',
              mt: 1,
              pt: 1,
              borderTop: (theme) => `1px solid ${theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.08)'
                : 'rgba(0, 0, 0, 0.08)'}`,
            }}
          >
            <Typography
              sx={{
                fontSize: '0.7rem',
                color: (theme) => theme.palette.mode === 'dark' ? '#FFC107' : 'text.primary',
                fontWeight: 600,
              }}
            >
              → Energy-only analysis understates value by {(energy_only_payback_months / displayPayback).toFixed(1)}×
            </Typography>
          </Box>
        </Box>
      )}

      {/* Value Breakdown Section - 5 corpus-validated components */}
      {hasComprehensiveValue && valueBreakdown && (
        <Box
          sx={{
            mt: 4,
            mb: 4,
            p: 3,
            borderRadius: 2,
            background: (theme) =>
              theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, rgba(78, 205, 196, 0.06), rgba(78, 205, 196, 0.1))'
                : 'linear-gradient(135deg, rgba(78, 205, 196, 0.04), rgba(78, 205, 196, 0.08))',
            border: (theme) => `1px solid ${theme.palette.mode === 'dark'
              ? 'rgba(78, 205, 196, 0.2)'
              : 'rgba(13, 115, 119, 0.25)'}`,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              mb: 2,
              color: (theme) => theme.palette.mode === 'dark' ? '#4ECDC4' : 'text.primary',
              fontSize: '0.875rem',
              letterSpacing: '0.02em',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            Value Breakdown
            <Typography
              component="span"
              sx={{
                fontSize: '0.7rem',
                fontWeight: 500,
                color: 'text.secondary',
                ml: 1,
              }}
            >
              (6 components — sources indicated per metric)
            </Typography>
          </Typography>
          <Grid container spacing={2}>
            {[
              { key: 'energy_savings', label: 'Energy Savings', value: valueBreakdown.energy_savings, colorDark: '#4CAF50', colorLight: '#5B7F24', description: '129 PE-certified measurements', source: 'PE-Certified', sourceColor: '#4CAF50' },
              { key: 'avoided_degradation', label: 'Avoided Degradation', value: valueBreakdown.avoided_degradation, colorDark: '#4ECDC4', colorLight: '#0D7377', description: 'ASHRAE 1998 Handbook Ch 2.9', source: 'ASHRAE', sourceColor: '#2196F3' },
              { key: 'avoided_demand', label: 'Avoided Demand', value: valueBreakdown.avoided_demand, colorDark: '#64B5F6', colorLight: '#1868DB', description: 'Peak kW demand charge reduction', source: 'Industry Est.', sourceColor: '#9E9E9E' },
              { key: 'avoided_runtime', label: 'Avoided Runtime', value: valueBreakdown.avoided_runtime, colorDark: '#FF8A65', colorLight: '#C74600', description: 'Reduced extended operation hours', source: 'Industry Est.', sourceColor: '#9E9E9E' },
              { key: 'life_extension', label: 'Life Extension', value: valueBreakdown.life_extension, colorDark: '#A0CE4E', colorLight: '#5B7F24', description: 'Deferred capital replacement', source: 'Industry Est.', sourceColor: '#9E9E9E' },
              { key: 'mission_critical_protection', label: 'Mission-Critical Protection', value: valueBreakdown.mission_critical_protection, colorDark: '#9C27B0', colorLight: '#7B1FA2', description: 'Avoided operational disruption', source: 'Industry Est.', sourceColor: '#9E9E9E' },
            ].map((item, idx) => (
              <Grid item xs={6} sm={4} md={4} key={idx}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 1,
                    background: (theme) => theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.04)',
                    borderLeft: (theme) => `3px solid ${theme.palette.mode === 'dark' ? item.colorDark : item.colorLight}`,
                    position: 'relative',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: (theme) => theme.palette.mode === 'dark' ? item.colorDark : 'text.primary',
                        fontWeight: 600,
                        fontSize: '0.7rem',
                      }}
                    >
                      {item.label}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => setOpenMetric(item.key)}
                      sx={{
                        p: 0.25,
                        color: 'text.disabled',
                        '&:hover': {
                          color: (theme) => theme.palette.mode === 'dark' ? item.colorDark : item.colorLight,
                          background: (theme) => `${theme.palette.mode === 'dark' ? item.colorDark : item.colorLight}22`
                        }
                      }}
                    >
                      <InfoOutlinedIcon sx={{ fontSize: 12 }} />
                    </IconButton>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: '1rem',
                      fontWeight: 700,
                      fontFamily: '"JetBrains Mono", monospace',
                      color: 'text.primary',
                    }}
                  >
                    {formatCurrency(item.value || 0)}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        fontSize: '0.6rem',
                      }}
                    >
                      {item.description}
                    </Typography>
                  </Box>
                  <Typography
                    component="span"
                    sx={{
                      display: 'inline-block',
                      mt: 0.5,
                      px: 0.75,
                      py: 0.125,
                      borderRadius: 1,
                      fontSize: '0.55rem',
                      fontWeight: 700,
                      letterSpacing: '0.03em',
                      color: '#fff',
                      backgroundColor: item.sourceColor,
                      lineHeight: 1.4,
                    }}
                  >
                    {item.source}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Year 1 Net Cash Flow - Only shown in 3-year mode */}
      {paymentMode === 'threeYear' && paymentMetrics?.threeYear && (
        <Card
          sx={{
            mt: 2,
            p: 3,
            borderTop: '3px solid',
            borderColor: paymentMetrics.threeYear.year1NetBenefit > 0 ? 'success.main' : 'warning.main',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            background: paymentMetrics.threeYear.year1NetBenefit > 0
              ? 'linear-gradient(135deg, rgba(76, 175, 80, 0.08), rgba(76, 175, 80, 0.04))'
              : undefined,
          }}
        >
          <Typography
            sx={{
              fontSize: '0.75rem',
              fontWeight: 500,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              color: 'text.disabled',
              mb: 1,
            }}
          >
            Year 1 Net Cash Flow
          </Typography>
          <Typography
            sx={{
              fontSize: '1.5rem',
              fontWeight: 700,
              color: paymentMetrics.threeYear.year1NetBenefit > 0 ? 'success.main' : 'warning.main',
              fontFamily: '"JetBrains Mono", monospace',
            }}
          >
            {paymentMetrics.threeYear.year1NetBenefit > 0 ? '+' : ''}
            {formatCurrency(paymentMetrics.threeYear.year1NetBenefit)}
          </Typography>
          {paymentMetrics.threeYear.year1NetBenefit > 0 && (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'success.main', fontWeight: 600 }}>
              Cash-flow positive from Day 1
            </Typography>
          )}
        </Card>
      )}

      {/* 5-Year Projection */}
      <Box
        sx={{
          mt: 4,
          p: 3,
          borderRadius: 2,
          background: (theme) =>
            theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, rgba(100, 181, 246, 0.08), rgba(100, 181, 246, 0.12))'
              : 'linear-gradient(135deg, rgba(25, 118, 210, 0.04), rgba(25, 118, 210, 0.08))',
          border: '1px solid',
          borderColor: 'primary.light',
        }}
      >
        <Typography
          variant="subtitle2"
          color="primary.main"
          sx={{ fontWeight: 600, mb: 2, fontSize: '0.875rem', letterSpacing: '0.02em' }}
        >
          5-Year Cumulative Impact
          {hasComprehensiveValue && (
            <Typography
              component="span"
              sx={{
                fontSize: '0.7rem',
                fontWeight: 500,
                color: 'text.secondary',
                ml: 1,
              }}
            >
              (comprehensive value)
            </Typography>
          )}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 3 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                {hasComprehensiveValue ? 'Total Value' : 'Total Savings'}
              </Typography>
              <IconButton
                size="small"
                onClick={() => setOpenMetric('five_year_cumulative_value')}
                sx={{
                  p: 0.25,
                  color: 'rgba(255,255,255,0.3)',
                  '&:hover': { color: 'primary.main', background: 'rgba(100, 181, 246, 0.1)' }
                }}
              >
                <InfoOutlinedIcon sx={{ fontSize: 12 }} />
              </IconButton>
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '1.5rem',
              }}
            >
              {formatCurrency(year_5_cumulative_value || year_5_cumulative)}
            </Typography>
            {hasComprehensiveValue && year_5_cumulative && (
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                ({formatCurrency(year_5_cumulative)} energy savings only)
              </Typography>
            )}
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                Net Profit (After Treatment)
              </Typography>
              <IconButton
                size="small"
                onClick={() => setOpenMetric('five_year_net_profit')}
                sx={{
                  p: 0.25,
                  color: 'rgba(255,255,255,0.3)',
                  '&:hover': { color: 'success.main', background: 'rgba(76, 175, 80, 0.1)' }
                }}
              >
                <InfoOutlinedIcon sx={{ fontSize: 12 }} />
              </IconButton>
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: 'success.main',
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '1.5rem',
              }}
            >
              {formatCurrency(year_5_net)}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Equipment Type Breakdown */}
      {activeTypes.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              mb: 2,
              color: 'text.secondary',
              fontSize: '0.75rem',
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
            }}
          >
            By Equipment Type
          </Typography>
          <Grid container spacing={2}>
            {activeTypes.map(([type, metrics], idx) => (
              <Grid
                item
                xs={12}
                sm={6}
                lg={3}
                key={type}
                sx={{
                  animation: `${fadeSlideUp} 0.5s ease-out`,
                  animationDelay: `${0.1 * (idx + 1)}s`,
                  animationFillMode: 'backwards',
                }}
              >
                <Card
                  sx={{
                    height: '100%',
                    bgcolor: 'background.paper',
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    overflow: 'hidden',
                    '&:hover': {
                      borderColor: 'rgba(160, 206, 78, 0.4)',
                      boxShadow: '0 12px 32px rgba(160, 206, 78, 0.15), 0 0 0 1px rgba(160, 206, 78, 0.1)',
                      transform: 'translateY(-2px)',
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '3px',
                      background: (theme) => theme.palette.mode === 'dark'
                        ? 'linear-gradient(90deg, #A0CE4E, #4ECDC4)'
                        : 'linear-gradient(90deg, #5B7F24, #0D7377)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    {/* Header: Icon + Type */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                      <Typography variant="h5" sx={{ fontSize: '1.75rem', lineHeight: 1 }}>
                        {equipmentIcons[type] || equipmentIcons.Unknown}
                      </Typography>
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 700,
                            fontSize: '1rem',
                            letterSpacing: '-0.01em',
                            color: 'text.primary',
                          }}
                        >
                          {type}{metrics.count > 1 ? 's' : ''}
                        </Typography>
                        <Chip
                          label={`${metrics.count} unit${metrics.count !== 1 ? 's' : ''}`}
                          size="small"
                          sx={{
                            mt: 0.5,
                            height: 20,
                            fontSize: '0.65rem',
                            backgroundColor: (theme) => theme.palette.mode === 'dark'
                              ? 'rgba(160, 206, 78, 0.15)'
                              : 'rgba(91, 127, 36, 0.12)',
                            color: (theme) => theme.palette.mode === 'dark' ? '#C5E87D' : '#5B7F24',
                            border: (theme) => `1px solid ${theme.palette.mode === 'dark'
                              ? 'rgba(160, 206, 78, 0.3)'
                              : 'rgba(91, 127, 36, 0.3)'}`,
                            fontWeight: 600,
                          }}
                        />
                      </Box>
                    </Box>

                    {/* Compact Metrics Grid */}
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6rem' }}>Tonnage</Typography>
                        <Typography sx={{ fontWeight: 700, color: 'text.primary', fontFamily: '"JetBrains Mono", monospace', fontSize: '0.875rem' }}>
                          {formatNumber(metrics.total_tonnage, 1)} tons
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6rem' }}>Treatment</Typography>
                        <Typography sx={{ fontWeight: 700, color: (theme) => theme.palette.mode === 'dark' ? '#FF6B35' : 'text.primary', fontFamily: '"JetBrains Mono", monospace', fontSize: '0.875rem' }}>
                          {formatCurrency(metrics.total_treatment_cost)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6rem' }}>Savings/yr</Typography>
                        <Typography sx={{ fontWeight: 700, color: (theme) => theme.palette.mode === 'dark' ? '#4ECDC4' : 'text.primary', fontFamily: '"JetBrains Mono", monospace', fontSize: '0.875rem' }}>
                          {formatCurrency(metrics.total_annual_savings)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6rem' }}>Payback</Typography>
                        <Typography sx={{ fontWeight: 700, color: (theme) => theme.palette.mode === 'dark' ? '#64B5F6' : 'text.primary', fontFamily: '"JetBrains Mono", monospace', fontSize: '0.875rem' }}>
                          {formatNumber(metrics.average_payback_months, 1)} mo
                        </Typography>
                      </Grid>
                    </Grid>

                    {/* ROI + Environmental */}
                    <Box
                      sx={{
                        mt: 1.5,
                        pt: 1.5,
                        borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Box>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6rem' }}>5-Yr ROI</Typography>
                        <Typography sx={{ fontWeight: 700, color: (theme) => theme.palette.mode === 'dark' ? '#A0CE4E' : 'text.primary', fontFamily: '"JetBrains Mono", monospace', fontSize: '0.875rem' }}>
                          {formatNumber(metrics.five_year_roi, 0)}%
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6rem' }}>kWh Saved</Typography>
                        <Typography sx={{ fontWeight: 600, color: (theme) => theme.palette.mode === 'dark' ? '#4ECDC4' : 'text.primary', fontFamily: '"JetBrains Mono", monospace', fontSize: '0.75rem' }}>
                          {formatNumber(metrics.total_kwh_saved)}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Metric Explanation Modal */}
      <MetricExplanationModal
        metric={openMetric}
        open={!!openMetric}
        onClose={() => setOpenMetric(null)}
      />
    </Box>
  );
}
